import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "../../../components/layout";
import sessionDB from "../../../utils/sessionDB";
import AddEmployeePage from "../../onboarding/Addemployeepage";
import CurrentEmployment from "../../onboarding/currentEmployment";
import ShiftMapping from "../../onboarding/shiftMapping";
import PreviousEmployment from "../../onboarding/PreviousEmployment";
import EducationRecords from "../../onboarding/educationRecords";
import EmployeeDocuments from "../../onboarding/EmployeeDocuments";
import AccountDetails from "../../onboarding/accountDetails";
import EmergencyInfo from "../../onboarding/EmergencyInfo";
import Assess from "../../onboarding/access/assess";
import PositionHistory from "../../onboarding/positionHistory";
import {
  ONBOARDING_ROUTE_CONFIG,
  getOnboardingNextRoute,
  getOnboardingPreviousRoute
} from "./onboardingRoutes";

function extractEmployeeId(payload) {
  const candidates = [
    payload?.employeeId,
    payload?.empId,
    payload?.emp_id,
    payload?.data?.employeeId,
    payload?.data?.empId,
    payload?.data?.emp_id,
    payload?.employee?.employeeId,
    payload?.employee?.empId,
    payload?.employee?.emp_id
  ];

  const match = candidates.find((value) => value !== null && value !== undefined && String(value).trim());
  return match ? String(match).trim() : "";
}

function getOnboardingEmployeeId() {
  return sessionDB.getSession().onboardingEmployeeId || "";
}

function setOnboardingEmployeeId(employeeId) {
  sessionDB.updateSession({ onboardingEmployeeId: employeeId ? String(employeeId) : "" });
}

function buildBreadcrumb(slug, navigate) {
  const currentIndex = ONBOARDING_ROUTE_CONFIG.findIndex((item) => item.slug === slug);

  return [
    {
      label: "Home",
      onClick: () => navigate("/")
    },
    {
      label: "Onboarding",
      onClick: () => navigate("/")
    },
    ...ONBOARDING_ROUTE_CONFIG.slice(0, currentIndex + 1).map((item, index) => ({
      label: item.label,
      active: index === currentIndex,
      onClick: index === currentIndex ? undefined : () => navigate(`/hrms-team/onboarding/${item.slug}`)
    }))
  ];
}

function OnboardingShell({ slug, children }) {
  const navigate = useNavigate();
  const breadcrumb = useMemo(() => buildBreadcrumb(slug, navigate), [navigate, slug]);

  return (
    <AppLayout hideBreadcrumb className="onboarding-shell-main">
      <div className="onboarding-shell-content w-full">
        {typeof children === "function" ? children({ navigate, breadcrumb }) : children}
      </div>
    </AppLayout>
  );
}

function useOnboardingNavigation(slug) {
  const navigate = useNavigate();

  return {
    goBack: useCallback(() => {
      navigate(getOnboardingPreviousRoute(slug));
    }, [navigate, slug]),
    goNext: useCallback(() => {
      const nextRoute = getOnboardingNextRoute(slug);
      if (nextRoute) {
        navigate(nextRoute);
      } else {
        navigate("/");
      }
    }, [navigate, slug])
  };
}

export function EmployeeInformationPage() {
  const navigate = useNavigate();
  const [toast, setToast] = useState("");

  const handleSuccess = useCallback((data) => {
    const employeeId = extractEmployeeId(data);
    if (employeeId) {
      setOnboardingEmployeeId(employeeId);
    }

    setToast(employeeId ? `Employee added. ID ${employeeId} saved for onboarding.` : "Employee added.");
    window.setTimeout(() => {
      setToast("");
      const nextRoute = getOnboardingNextRoute("employee-information");
      navigate(nextRoute || "/");
    }, 1400);
  }, [navigate]);

  return (
    <OnboardingShell slug="employee-information">
      {({ navigate: nav, breadcrumb }) => (
        <>
          <AddEmployeePage
            accessToken={sessionDB.getAccessToken()}
            tenantCode={sessionDB.getTenantCode()}
            onBack={() => nav("/")}
            onSuccess={handleSuccess}
            breadcrumb={breadcrumb}
          />
          <div
            className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[500] bg-foreground text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg transition-opacity pointer-events-none whitespace-nowrap ${
              toast ? "opacity-100" : "opacity-0"
            }`}
            role="status"
            aria-live="polite"
          >
            {toast}
          </div>
        </>
      )}
    </OnboardingShell>
  );
}

export function CurrentEmploymentPage() {
  const { goBack, goNext } = useOnboardingNavigation("current-employment");

  return (
    <OnboardingShell slug="current-employment">
      {({ breadcrumb }) => (
        <CurrentEmployment
          accessToken={sessionDB.getAccessToken()}
          tenantCode={sessionDB.getTenantCode()}
          initialEmployeeId={getOnboardingEmployeeId()}
          onBack={goBack}
          onNext={goNext}
          breadcrumb={breadcrumb}
        />
      )}
    </OnboardingShell>
  );
}

export function ShiftMappingPage() {
  const { goBack, goNext } = useOnboardingNavigation("shift-mapping");

  return (
    <OnboardingShell slug="shift-mapping">
      {({ breadcrumb }) => (
        <ShiftMapping
          accessToken={sessionDB.getAccessToken()}
          tenantCode={sessionDB.getTenantCode()}
          initialEmployeeId={getOnboardingEmployeeId()}
          breadcrumb={breadcrumb}
          onBack={goBack}
          onNext={goNext}
        />
      )}
    </OnboardingShell>
  );
}

export function PreviousEmploymentPage() {
  const { goBack, goNext } = useOnboardingNavigation("previous-employment");

  return (
    <OnboardingShell slug="previous-employment">
      {({ breadcrumb }) => (
        <PreviousEmployment
          accessToken={sessionDB.getAccessToken()}
          tenantCode={sessionDB.getTenantCode()}
          initialEmployeeId={getOnboardingEmployeeId()}
          breadcrumb={breadcrumb}
          onBack={goBack}
          onNext={goNext}
        />
      )}
    </OnboardingShell>
  );
}

export function EducationRecordsPage() {
  const { goBack, goNext } = useOnboardingNavigation("education-records");

  return (
    <OnboardingShell slug="education-records">
      {({ breadcrumb }) => (
        <EducationRecords
          accessToken={sessionDB.getAccessToken()}
          tenantCode={sessionDB.getTenantCode()}
          initialEmployeeId={getOnboardingEmployeeId()}
          breadcrumb={breadcrumb}
          onBack={goBack}
          onNext={goNext}
        />
      )}
    </OnboardingShell>
  );
}

export function EmployeeDocumentsPage() {
  const { goBack, goNext } = useOnboardingNavigation("employee-documents");

  return (
    <OnboardingShell slug="employee-documents">
      {({ breadcrumb }) => (
        <EmployeeDocuments
          accessToken={sessionDB.getAccessToken()}
          tenantCode={sessionDB.getTenantCode()}
          initialEmployeeId={getOnboardingEmployeeId()}
          onBack={goBack}
          onNext={goNext}
          breadcrumb={breadcrumb}
        />
      )}
    </OnboardingShell>
  );
}

export function AccountDetailsPage() {
  const { goBack, goNext } = useOnboardingNavigation("account-details");

  return (
    <OnboardingShell slug="account-details">
      {({ breadcrumb }) => (
        <AccountDetails
          accessToken={sessionDB.getAccessToken()}
          tenantCode={sessionDB.getTenantCode()}
          initialEmployeeId={getOnboardingEmployeeId()}
          onBack={goBack}
          onNext={goNext}
          breadcrumb={breadcrumb}
        />
      )}
    </OnboardingShell>
  );
}

export function EmergencyInfoPage() {
  const { goBack, goNext } = useOnboardingNavigation("emergency-info");

  return (
    <OnboardingShell slug="emergency-info">
      {({ breadcrumb }) => (
        <EmergencyInfo
          accessToken={sessionDB.getAccessToken()}
          tenantCode={sessionDB.getTenantCode()}
          initialEmployeeId={getOnboardingEmployeeId()}
          onBack={goBack}
          onNext={goNext}
          breadcrumb={breadcrumb}
        />
      )}
    </OnboardingShell>
  );
}

export function AssetsAccessPage() {
  const { goBack } = useOnboardingNavigation("assets-access");
  const navigate = useNavigate();

  return (
    <OnboardingShell slug="assets-access">
      {({ breadcrumb }) => (
        <Assess
          onBack={goBack}
          onNext={() => navigate("/")}
          breadcrumb={breadcrumb}
        />
      )}
    </OnboardingShell>
  );
}

export function PositionHistoryPage() {
  const { goBack, goNext } = useOnboardingNavigation("position-history");

  return (
    <OnboardingShell slug="position-history">
      {({ breadcrumb }) => (
        <PositionHistory
          accessToken={sessionDB.getAccessToken()}
          tenantCode={sessionDB.getTenantCode()}
          initialEmployeeId={getOnboardingEmployeeId()}
          onBack={goBack}
          onNext={goNext}
          breadcrumb={breadcrumb}
        />
      )}
    </OnboardingShell>
  );
}
